import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { API, authFetch } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import PortalLayout from "../components/PortalLayout";
import DocumentPreview from "../components/DocumentPreview";
import {
  Upload,
  FileText,
  Trash2,
  Eye,
  AlertCircle,
  CheckCircle,
  File,
  Loader2,
  FolderLock,
} from "lucide-react";

// FY options starting 2021-22, default 2026-27
const FY_OPTIONS = [];
for (let y = 2021; y <= new Date().getFullYear() + 2; y++) {
  FY_OPTIONS.push(`${y}-${String(y + 1).slice(-2)}`);
}
const DEFAULT_FY = "2026-27";

const DOC_CATEGORIES = [
  { value: "form16", label: "Form 16" },
  { value: "ais", label: "AIS / TIS" },
  { value: "bank_statement", label: "Bank Statement" },
  { value: "investment_proof", label: "Investment Proof" },
  { value: "other", label: "Other" },
];

const TEAM_DOC_CATEGORIES = [
  { value: "itr_copy", label: "ITR Copy" },
  { value: "computation", label: "Computation of Income" },
  { value: "acknowledgement", label: "Filing Acknowledgement" },
  { value: "other_team", label: "Other" },
];

const ITRFilingView = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState("client");
  const [selectedFY, setSelectedFY] = useState(DEFAULT_FY);
  const [uploadForm, setUploadForm] = useState({ file: null, category: "", notes: "", profile_id: "" });
  const [preview, setPreview] = useState(null);

  // Fetch profiles on mount
  useEffect(() => {
    authFetch(`${API}/profiles`).then(r => r.ok ? r.json() : []).then(setProfiles).catch(() => {});
  }, []);

  const fetchDocuments = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedFY && selectedFY !== "all") params.append("fy", selectedFY);
      if (activeTab === "client") params.append("uploaded_by", "client");
      else params.append("uploaded_by", "team");

      const res = await authFetch(`${API}/documents/my-documents?${params}`);
      if (res.ok) {
        const docs = await res.json();
        setDocuments(Array.isArray(docs) ? docs.filter((d) => d.service_type === "itr_filing") : []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [selectedFY, activeTab]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file || !uploadForm.category) {
      toast.error("Select a file and category");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", uploadForm.file);
    fd.append("document_category", uploadForm.category);
    fd.append("service_type", "itr_filing");
    fd.append("financial_year", selectedFY === "all" ? DEFAULT_FY : selectedFY);
    fd.append("uploaded_by", "client");
    if (uploadForm.notes) fd.append("notes", uploadForm.notes);
    if (uploadForm.profile_id) fd.append("profile_id", uploadForm.profile_id);

    try {
      const res = await authFetch(`${API}/documents/upload`, { method: "POST", body: fd });
      if (res.ok) {
        toast.success("Document uploaded");
        setUploadForm({ file: null, category: "", notes: "", profile_id: "" });
        fetchDocuments();
      } else {
        toast.error("Upload failed");
      }
    } catch {
      toast.error("An error occurred");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      const res = await authFetch(`${API}/documents/${docId}`, { method: "DELETE" });
      if (res.ok) { toast.success("Deleted"); fetchDocuments(); }
    } catch { toast.error("Failed"); }
  };

  const getCategoryLabel = (val) => {
    const all = [...DOC_CATEGORIES, ...TEAM_DOC_CATEGORIES];
    return all.find((c) => c.value === val)?.label || val;
  };

  const getProfileName = (profileId) => {
    if (!profileId) return null;
    const p = profiles.find((pr) => pr.profile_id === profileId);
    return p?.full_name || null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg-primary)" }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <PortalLayout title="ITR Filing">
      <div className="relative z-10 w-full pb-4 overflow-hidden">
        {/* Security Banner */}
        <div className="flex items-center gap-2 mb-3 p-4 rounded-[20px] bg-white border border-[var(--border-light)] shadow-sm" data-testid="security-badge-itr">
          <FolderLock className="w-5 h-5 text-[var(--success)] shrink-0" />
          <span className="text-sm font-medium text-[var(--text-muted)]">
            <strong className="text-[var(--text-main)] text-sm">Secured Vault:</strong> AES-256 Encrypted Storage &middot; Cloudflare-Powered Infrastructure &middot; Private Document Custody
          </span>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-3 soft-card !p-3">
          <div className="flex gap-2 p-1.5 rounded-full bg-[var(--background-primary)] border border-[var(--border-light)]">
            {[
              { key: "client", label: "Client Documents" },
              { key: "team", label: "Team Documents" },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-5 py-2 text-sm font-bold rounded-full transition-all duration-300 ${
                  activeTab === tab.key ? "bg-[var(--primary)] text-white shadow-md" : "text-[var(--text-muted)] hover:bg-[var(--primary)]/5"
                }`}
                data-testid={`itr-tab-${tab.key}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3 px-3">
            <span className="text-sm font-bold text-[var(--text-muted)]">Financial Year:</span>
            <Select value={selectedFY} onValueChange={setSelectedFY}>
              <SelectTrigger className="w-[140px] rounded-xl border-[var(--border-light)] font-medium" data-testid="itr-fy-filter">
                <SelectValue placeholder="Select FY" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                {FY_OPTIONS.map((fy) => (
                  <SelectItem key={fy} value={fy}>{fy}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-4 min-w-0">
          {/* Upload Section (only on client tab) */}
          {activeTab === "client" && (
            <div className="lg:col-span-1 min-w-0 overflow-hidden">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="soft-card self-start h-fit"
              >
                <h2 className="font-['Playfair_Display'] text-xl font-bold mb-3 text-[var(--text-main)]">
                  Upload Document
                </h2>

                <form onSubmit={handleUpload} className="space-y-4">
                  <div
                    className="p-6 rounded-[20px] border-2 border-dashed border-[var(--border-light)] bg-[var(--background-primary)] text-center cursor-pointer hover:border-[var(--primary)] transition-all duration-300"
                  >
                    <input
                      type="file"
                      id="itr-upload"
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx"
                      onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                      data-testid="itr-file-input"
                    />
                    <label htmlFor="itr-upload" className="cursor-pointer flex flex-col items-center">
                      <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-sm border border-[var(--border-light)] mb-4 group-hover:scale-105 transition-transform">
                        <Upload className="w-6 h-6 text-[var(--primary)]" />
                      </div>
                      {uploadForm.file ? (
                        <span className="text-sm font-bold text-[var(--text-main)]">
                          {uploadForm.file.name}
                        </span>
                      ) : (
                        <>
                          <span className="text-sm font-bold text-[var(--text-main)]">
                            Form 16, AIS, Bank Statements
                          </span>
                          <span className="text-xs font-medium mt-1 text-[var(--text-muted)]">
                            PDF, JPG, PNG, XLS
                          </span>
                        </>
                      )}
                    </label>
                  </div>

                  <Select value={uploadForm.category} onValueChange={(v) => setUploadForm({ ...uploadForm, category: v })}>
                    <SelectTrigger data-testid="itr-doc-category" className="rounded-xl border-[var(--border-light)] hover:border-[var(--primary)] transition-all cursor-pointer">
                      <SelectValue placeholder="Document Type" />
                    </SelectTrigger>
                    <SelectContent>
                      {DOC_CATEGORIES.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {profiles.length > 0 && (
                    <Select value={uploadForm.profile_id} onValueChange={(v) => setUploadForm({ ...uploadForm, profile_id: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="itr-profile-select" className="rounded-xl border-[var(--border-light)] hover:border-[var(--primary)] transition-all cursor-pointer">
                        <SelectValue placeholder="Tag a Profile (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No profile tag</SelectItem>
                        {profiles.map((p) => (
                          <SelectItem key={p.profile_id} value={p.profile_id}>{p.full_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                  <Input
                    value={uploadForm.notes}
                    onChange={(e) => setUploadForm({ ...uploadForm, notes: e.target.value })}
                    placeholder="Notes (optional)"
                    className="w-full rounded-xl border-[var(--border-light)]"
                  />

                  <button
                    type="submit"
                    disabled={uploading || !uploadForm.file}
                    className="soft-btn w-full mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="itr-upload-btn"
                  >
                    {uploading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Upload className="w-5 h-5 mr-2" />}
                    {uploading ? "Uploading..." : "Upload Document"}
                  </button>
                </form>
              </motion.div>
            </div>
          )}

          {/* Documents List */}
          <div className={activeTab === "client" ? "lg:col-span-2 min-w-0 overflow-hidden" : "lg:col-span-3 min-w-0 overflow-hidden"}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="soft-card h-full"
              data-testid="itr-documents-list"
            >
              <h2 className="font-['Playfair_Display'] text-xl font-bold mb-3 text-[var(--text-main)]">
                {activeTab === "client" ? "Documents Provided by You" : "Final Documents from Team"}
                {selectedFY && selectedFY !== "all" ? ` (FY ${selectedFY})` : ""}
              </h2>

              {documents.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Filename</TableHead>
                        <TableHead>Profile</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>FY</TableHead>
                        <TableHead>Notes</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {documents.map((doc) => (
                        <TableRow key={doc.document_id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded bg-[var(--primary)]/10 flex items-center justify-center">
                                <FileText className="w-4 h-4 text-[var(--primary)]" />
                              </div>
                              <span className="text-sm font-bold text-[var(--text-main)]">
                                {doc.filename}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {getProfileName(doc.profile_id) ? (
                              <span className="px-3 py-1 text-xs font-bold rounded-full bg-white border border-[var(--border-light)] shadow-sm text-[var(--text-main)]">
                                {getProfileName(doc.profile_id)}
                              </span>
                            ) : (
                              <span className="text-xs font-medium text-[var(--text-muted)]">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="px-3 py-1 text-xs font-bold rounded-full bg-[var(--success)]/10 text-[var(--success)]">
                              {getCategoryLabel(doc.document_category)}
                            </span>
                          </TableCell>
                          <TableCell className="text-sm" style={{ color: "var(--text-secondary)" }}>
                            {doc.financial_year || "-"}
                          </TableCell>
                          <TableCell className="text-sm" style={{ color: "var(--text-secondary)" }}>
                            {doc.notes || "-"}
                          </TableCell>
                          <TableCell className="text-sm" style={{ color: "var(--text-secondary)" }}>
                            {new Date(doc.uploaded_at).toLocaleDateString("en-IN")}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              {doc.file_url && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setPreview({ fileUrl: doc.file_url, filename: doc.filename, contentType: doc.content_type || doc.file_type })}
                                  className="text-[var(--accent)] hover:bg-[var(--accent-bg)] transition-all duration-300"
                                  data-testid={`preview-${doc.document_id}`}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                              )}
                              {activeTab === "client" && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(doc.document_id)}
                                  className="text-[#EF4444] hover:bg-[#EF4444]/10 transition-all duration-300"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-16 px-4 bg-[var(--background-primary)] border border-dashed border-[var(--border-light)] rounded-[20px]">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-white shadow-sm border border-[var(--border-light)] flex items-center justify-center">
                    <File className="w-10 h-10 text-[var(--text-muted)]/50" />
                  </div>
                  <p className="font-bold text-lg mb-2 text-[var(--text-main)]">
                    {activeTab === "client" ? "No documents uploaded yet" : "Your team hasn't shared documents yet"}
                  </p>
                  <p className="text-sm mb-3 max-w-sm mx-auto text-[var(--text-muted)]">
                    {activeTab === "client"
                      ? "Upload your PAN, Aadhaar, Form 16, or other tax documents to get started with your ITR filing."
                      : "Documents from your CA team will appear here once they are uploaded."}
                  </p>
                  {activeTab === "client" && (
                    <label htmlFor="itr-upload" className="soft-btn cursor-pointer inline-flex">
                      <Upload className="w-4 h-4 mr-2" /> Upload Your First Document
                    </label>
                  )}
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>

      {/* Document Preview Modal */}
      {preview && (
        <DocumentPreview
          fileUrl={preview.fileUrl}
          filename={preview.filename}
          contentType={preview.contentType}
          onClose={() => setPreview(null)}
        />
      )}
    </PortalLayout>
  );
};

export default ITRFilingView;
